
import { CheckCircleOutlined, ReportProblemOutlined } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";

import type { WidgetProps } from "../types";
import { formatGeneratedAt } from "./format";

// Live-data widget #2: per-ASIN suppression reasons + corrective actions.
// Frames as "here's what we'd unblock" — opportunity-positioning, not alarm.
export function SuppressionDiagnoses({ widget }: WidgetProps<"suppression_diagnoses">) {
    const { props, data } = widget;
    const title = props.title ?? "Listings we can unblock";

    if (data.items.length === 0) {
        return (
            <Box
                sx={{
                    p: 3,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "10px",
                    bgcolor: "background.paper",
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                }}
            >
                <Box sx={{ color: "primary.main", mt: 0.25 }}>
                    <CheckCircleOutlined />
                </Box>
                <Box>
                    <Typography variant="h5" sx={{ mb: 0.5 }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        No suppressed listings detected on your catalog. Snapshot{" "}
                        {formatGeneratedAt(data.generatedAt)}.
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                {data.items.length} listing{data.items.length === 1 ? "" : "s"} · snapshot{" "}
                {formatGeneratedAt(data.generatedAt)}
            </Typography>

            <Stack spacing={1.5}>
                {data.items.map((item) => (
                    <Box
                        key={item.asin}
                        sx={{
                            p: 2.5,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: "10px",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems="flex-start"
                        >
                            <Box sx={{ color: "secondary.main", mt: 0.25 }}>
                                <ReportProblemOutlined />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="baseline"
                                    sx={{ flexWrap: "wrap", mb: 1 }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily: "monospace",
                                            fontSize: "0.85rem",
                                            color: "text.primary",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {item.asin}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {item.title}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Box component="strong" sx={{ color: "text.primary", mr: 0.5 }}>
                                        Reason:
                                    </Box>
                                    {item.reason}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <Box
                                        component="strong"
                                        sx={{ color: "primary.main", mr: 0.5 }}
                                    >
                                        Next step:
                                    </Box>
                                    {item.correctiveAction}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}
